import { canv } from "@/canv/global.js";

import { isSeveralEntityTypesInSelected } from "@/canv/utils";
import { getFirstSelected } from "@/canvasController/helpers";
import { AI_ASSISTANT_SUPPORTED_WIDGETS } from "@/constants";
import { EmbedWidget } from "@/elements/embed";
import { eventEmitter } from "@/eventEmitter";
import type { Group } from "@/group";
import type { BorderOption, LineOption } from "@/interface/WidgetMenu";
import type { WidgetOptions } from "@/shared/types/widgetOptions";

function isWidgetsSupportsAi(groups: Group[]) {
    for (const group of groups) {
        if (group.isItemWrapper()) {
            const child = group.onlyChild();

            if (child && AI_ASSISTANT_SUPPORTED_WIDGETS.includes(child.constructorName)) {
                return true;
            }

            continue;
        }

        for (const innerGroup of group.children) {
            if ("onlyChild" in innerGroup) {
                const child = innerGroup.onlyChild();

                if (child && AI_ASSISTANT_SUPPORTED_WIDGETS.includes(child.constructorName)) {
                    return true;
                }
            }
        }
    }

    return false;
}

function buildSelectionKey(groups: Group[], subKey: string) {
    const ids = groups.map((group) => group.UID).join("|");
    return subKey ? `${ids}::${subKey}` : ids;
}

//todo все типы и интерфейсы переедут в отдельный файл когда будет заканчиваться перенос на TS

export interface DocumentPreviewPageOption {
    previewPageNumber: number;
    currentPageNumber: number;
}

export interface DocumentTurnPageOption {
    currentPageNumber: number;
    totalPages: number;
}

export class widgetOption<
    T =
        | string
        | number
        | null
        | object
        | boolean
        | LineOption
        | BorderOption
        | DocumentPreviewPageOption
        | DocumentTurnPageOption,
> {
    constructor(name: WidgetOptions, value: T, UID: string) {
        this.name = name;
        this.value = value;
        this.UID = UID;
    }

    getState() {
        return {
            value: this.value,
            name: this.name,
            UID: this.UID,
        };
    }

    name: WidgetOptions;
    value: T;
    UID: string;
}

let isMenuOpened = false;

const TEXT_FORMAT_OPTIONS = ["textBold", "textStrike"];

export class widgetMenu {
    // external API

    // тяжелая функция из-за работы с DOM
    reopen() {
        if (canv.isBoardModeViewCopy()) {
            return;
        }

        if (canv.canvasController.selected.length === 0) {
            this.hide(); // если нету выбранных entity убеждаемся, что и меню нету. Нужно, так как после deleteProcessor вызывается reopen, а не hide
            return;
        }

        isMenuOpened = true;

        this.collectWidgetOptions();

        setTimeout(this.TryLocateMenu.bind(this), 50);
    }

    recollectWidgetOptionsMap() {
        if (!isMenuOpened) {
            return;
        }

        this.allOptions = [];

        const options: Array<widgetOption[]> = [];
        for (let p = 0; p < canv.canvasController.selected.length; p++) {
            canv.canvasController.selected[p].collectWidgetOptions(options);
        }

        const optionsUniqs: Array<string[]> = [];
        const plainOptions: widgetOption[] = [];
        for (let i = 0; i < options.length; i++) {
            const uniqs: string[] = [];
            for (let k = 0; k < options[i].length; k++) {
                plainOptions.push(options[i][k]);
                const index = uniqs.indexOf(options[i][k].name);
                if (index === -1) {
                    uniqs.push(options[i][k].name);
                }
            }
            optionsUniqs.push(uniqs);
        }

        for (let i = 0; i < plainOptions.length; i++) {
            const name = plainOptions[i].name;
            let foundInEvery = true;
            for (let k = 0; k < optionsUniqs.length; k++) {
                const index = optionsUniqs[k].indexOf(name);
                if (index === -1) {
                    foundInEvery = false;
                }
            }

            if (foundInEvery) {
                this.allOptions.push(plainOptions[i]);
            }
        }

        const optionsMap: Map<WidgetOptions, widgetOption[]> = new Map();

        for (const option of this.allOptions) {
            if (
                option.name === "link" &&
                (canv.canvasController.selected.length > 1 ||
                    !canv.canvasController.selected[0].isItemWrapper())
            ) {
                continue;
            }

            if (!optionsMap.get(option.name)) {
                optionsMap.set(option.name, []);
            }

            optionsMap.get(option.name)?.push(option);
        }

        eventEmitter.dispatch("menu.widget.options", { options: optionsMap });
    }

    collectWidgetOptions() {
        if (!isMenuOpened) {
            return;
        }

        this.allOptions = [];

        const options: Array<widgetOption[]> = [];
        for (let p = 0; p < canv.canvasController.selected.length; p++) {
            canv.canvasController.selected[p].collectWidgetOptions(options);
        }

        const optionsUniqs: Array<string[]> = [];
        const plainOptions: widgetOption[] = [];
        for (let i = 0; i < options.length; i++) {
            const uniqs: string[] = [];
            for (let k = 0; k < options[i].length; k++) {
                plainOptions.push(options[i][k]);
                const index = uniqs.indexOf(options[i][k].name);
                if (index === -1) {
                    uniqs.push(options[i][k].name);
                }
            }
            optionsUniqs.push(uniqs);
        }

        for (let i = 0; i < plainOptions.length; i++) {
            const name = plainOptions[i].name;
            let foundInEvery = true;
            for (let k = 0; k < optionsUniqs.length; k++) {
                const index = optionsUniqs[k].indexOf(name);
                if (index === -1) {
                    foundInEvery = false;
                }
            }

            if (foundInEvery) {
                this.allOptions.push(plainOptions[i]);
            }
        }

        const optionsMap: Map<WidgetOptions, widgetOption[]> = new Map();
        const availableOptions: Set<WidgetOptions> = new Set();

        for (const option of this.allOptions) {
            if (
                option.name === "link" &&
                (canv.canvasController.selected.length > 1 ||
                    !canv.canvasController.selected[0].isItemWrapper())
            ) {
                continue;
            }

            if (option.name === "frameAspectRatio" && canv.canvasController.selected.length > 1) {
                continue;
            }

            if (TEXT_FORMAT_OPTIONS.includes(option.name)) {
                availableOptions.add("textFormat");
            } else {
                availableOptions.add(option.name);
            }

            if (!optionsMap.get(option.name)) {
                optionsMap.set(option.name, []);
            }

            optionsMap.get(option.name)?.push(option);
        }

        eventEmitter.dispatch("menu.widget.options", { options: optionsMap });

        let useGroupOptions = true;
        const cursorTool = canv.toolBox.TryGetToolByName("cursorTool");

        if (cursorTool) {
            const isOnlyEmbedWidget =
                canv.canvasController.selected.length === 1 &&
                canv.canvasController.selected[0].onlyChild() instanceof EmbedWidget;

            if (cursorTool.isMode("interaction") && !isOnlyEmbedWidget) {
                useGroupOptions = false;
            } else if (
                canv.canvasController.selected.length === 1 &&
                canv.canvasController.selected[0]?.onlyChild()?.constructorName === "comment"
            ) {
                useGroupOptions = false;
            }
        }

        if (useGroupOptions) {
            // align
            if (canv.canvasController.selected.length !== 1) {
                availableOptions.add("align");
            }

            // group
            if (
                canv.canvasController.selected.length !== 1 ||
                (!canv.canvasController.selected[0].isItemWrapper() &&
                    !canv.canvasController.selected[0].isLocked)
            ) {
                availableOptions.add("group");
            }

            // lock ----
            availableOptions.add("lock");

            // filter ----
            if (isSeveralEntityTypesInSelected(canv.canvasController.selected)) {
                availableOptions.add("filter");
            }
            // ----------

            // delete ----
            availableOptions.add("delete");

            // aiAssistant ----
            if (
                !canv.getInitDataProp("isOnPrem") &&
                isWidgetsSupportsAi(canv.canvasController.selected)
            ) {
                availableOptions.add("aiAssistant");
            }

            // context menu ----
            availableOptions.add("contextMenu");
            // ----------
        }

        eventEmitter.dispatch("menu.widget.availableOptions", { options: [...availableOptions] });
    }

    hide() {
        eventEmitter.dispatch("menu.widget.coordinates", { coordinates: null });

        isMenuOpened = false;
    }

    TryLocateMenu() {
        const selectedProps = canv.canvasController.getSelectedProps();

        if (!isMenuOpened) {
            return;
        }

        const startX = selectedProps.startX;
        const startY = selectedProps.startY;
        const width = selectedProps.width;

        let xTarget = (startX + width / 2) * canv.m[0];
        let yTarget = startY * canv.m[3];

        const cursorTool = canv.toolBox.TryGetToolByName("cursorTool");

        let interactionKey = "";

        if (cursorTool.isMode("interaction")) {
            const interactionLocale = getFirstSelected().locateWidgetMenu();
            if (interactionLocale?.status) {
                xTarget = interactionLocale.x * canv.m[3];
                yTarget = interactionLocale.y * canv.m[3];
                interactionKey = `${xTarget}:${yTarget}`;
            }
        }

        const canvasMatrixKey = canv.m.map((n) => n.toFixed(3)).join(",");
        const selectionKey = buildSelectionKey(canv.canvasController.selected, interactionKey);

        eventEmitter.dispatch("menu.widget.coordinates", {
            coordinates: { top: yTarget, left: xTarget, canvasMatrixKey, selectionKey },
        });
    }

    allOptions: widgetOption[] = [];
    colorPickOpen = false;
}
